import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ZoomInIcon, BuildingIcon, RulerIcon, LayersIcon } from 'lucide-react';
export function TechnicalPlans() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const plans = [{
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/6kaNeo5ZN82XvvfBoLQ1yd/plan_cuza_voda_50.png',
    title: 'Plan tehnic Clădire 1',
    subtitle: 'Cuza Vodă 50',
    details: 'D+P+6E – suprafață desfășurată 1.196 mp'
  }, {
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/a4ZeiFpDVRGmx3TCVDYgVK/planuri_cuza_voda_52.png',
    title: 'Plan tehnic Clădire 2',
    subtitle: 'Cuza Vodă 52',
    details: 'S+P+7E – suprafață desfășurată 1.309 mp'
  }];
  const specifications = [{
    icon: BuildingIcon,
    label: 'Suprafață construită desfășurată totală',
    value: '2.500 mp'
  }, {
    icon: RulerIcon,
    label: 'Suprafață teren',
    value: '395 mp'
  }, {
    icon: LayersIcon,
    label: 'Cuza Vodă 50',
    value: 'D+P+6E, 1.196 mp'
  }, {
    icon: LayersIcon,
    label: 'Cuza Vodă 52',
    value: 'S+P+7E, 1.309 mp'
  }, {
    icon: BuildingIcon,
    label: 'Structură',
    value: 'Beton armat'
  }, {
    icon: RulerIcon,
    label: 'Suprafață utilă / etaj',
    value: '110–130 mp'
  }];
  const features = ['Intrări separate pe fiecare corp', 'Funcțiuni actuale: birouri, spații medicale, zone tehnice', 'Posibilitate de compartimentare flexibilă pe niveluri'];
  return <section className="py-20 px-6 md:px-12 lg:px-16 bg-gradient-to-b from-white to-gray-50" data-id="element-482">
      <div className="max-w-7xl mx-auto" ref={ref} data-id="element-483">
        {/* Header */}
        <motion.div className="text-center mb-16" initial={{
        y: -30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-484">
          <h2 className="text-3xl md:text-5xl font-bold text-[#121c53] mb-4" data-id="element-485">
            Planuri tehnice și detalii{' '}
            <span className="text-[#357beb]" data-id="element-486">arhitecturale</span>
          </h2>
          <div className="w-24 h-1 bg-[#357beb] mx-auto mb-6" data-id="element-487"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium" data-id="element-488">
            Înțelege structura și organizarea spațiilor – baza solidă a unei
            investiții sigure.
          </p>
        </motion.div>
        {/* Introduction */}
        <motion.div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-16 border border-gray-100" initial={{
        y: 30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-489">
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-490">
            Imobilul Cuza Vodă 50–52 este alcătuit din două clădiri
            independente, construite pe o structură solidă din beton armat, cu o
            compartimentare eficientă și flexibilă. Planurile tehnice oferă o
            imagine clară asupra suprafețelor, etajelor și posibilităților de
            reconversie în funcție de nevoile investitorului.
          </p>
        </motion.div>
        {/* Technical Plans Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-491">
          {plans.map((plan, index) => <motion.div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 hover:border-[#357beb] transition-all duration-300" initial={{
          y: 50,
          opacity: 0
        }} animate={inView ? {
          y: 0,
          opacity: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3 + index * 0.1
        }} data-id="element-492">
              <div className="relative aspect-square overflow-hidden group cursor-pointer" data-id="element-493">
                <img src={plan.url} alt={plan.title} className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110" onClick={() => setSelectedImage(plan.url)} data-id="element-494" />
                <div className="absolute inset-0 bg-[#121c53]/0 group-hover:bg-[#121c53]/10 transition-all duration-300 flex items-center justify-center" data-id="element-495">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-496" />
                </div>
              </div>
              <div className="p-6 bg-gradient-to-br from-gray-50 to-white" data-id="element-497">
                <h3 className="text-xl font-bold text-[#121c53] mb-1" data-id="element-498">
                  {plan.title}
                </h3>
                <p className="text-[#357beb] font-semibold mb-2" data-id="element-499">
                  {plan.subtitle}
                </p>
                <p className="text-gray-600 text-sm" data-id="element-500">{plan.details}</p>
              </div>
            </motion.div>)}
        </div>
        {/* Technical Specifications */}
        <motion.div className="mb-16" initial={{
        y: 50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.5
      }} data-id="element-501">
          <h3 className="text-2xl md:text-3xl font-bold text-[#121c53] text-center mb-8" data-id="element-502">
            Specificații tehnice
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-503">
            {specifications.map((spec, index) => <motion.div key={index} className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:border-[#357beb] hover:shadow-lg transition-all duration-300" initial={{
            scale: 0.9,
            opacity: 0
          }} animate={inView ? {
            scale: 1,
            opacity: 1
          } : {}} transition={{
            duration: 0.5,
            delay: 0.6 + index * 0.05
          }} data-id="element-504">
                <div className="flex items-start gap-4" data-id="element-505">
                  <div className="p-3 bg-[#357beb]/10 rounded-lg" data-id="element-506">
                    <spec.icon className="w-6 h-6 text-[#357beb]" data-id="element-507" />
                  </div>
                  <div className="flex-1" data-id="element-508">
                    <p className="text-sm text-gray-600 mb-1" data-id="element-509">{spec.label}</p>
                    <p className="text-lg font-bold text-[#121c53]" data-id="element-510">
                      {spec.value}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
        {/* Additional Features */}
        <motion.div className="bg-gradient-to-br from-[#121c53] to-[#1a3a6b] rounded-2xl p-8 md:p-12 mb-12" initial={{
        scale: 0.95,
        opacity: 0
      }} animate={inView ? {
        scale: 1,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.8
      }} data-id="element-511">
          <div className="space-y-4" data-id="element-512">
            {features.map((feature, index) => <div key={index} className="flex items-center gap-3" data-id="element-513">
                <div className="w-2 h-2 bg-[#357beb] rounded-full" data-id="element-514"></div>
                <p className="text-white text-lg" data-id="element-515">{feature}</p>
              </div>)}
          </div>
        </motion.div>
        {/* Closing Statement */}
        <motion.div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border-l-4 border-[#357beb]" initial={{
        y: 30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 1
      }} data-id="element-516">
          <p className="text-lg text-gray-700 leading-relaxed" data-id="element-517">
            Structura modulară a celor două clădiri permite o adaptare rapidă a
            spațiilor pentru diverse utilizări: birouri, clinici, centre
            educaționale sau reconversie în apartamente moderne. Planurile
            tehnice evidențiază potențialul ridicat al imobilului pentru
            investiții sigure și dezvoltări urbane inteligente.
          </p>
        </motion.div>
      </div>
      {/* Lightbox Modal */}
      {selectedImage && <motion.div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} onClick={() => setSelectedImage(null)} data-id="element-518">
          <motion.img src={selectedImage} alt="Plan tehnic detaliat" className="max-w-full max-h-full object-contain" initial={{
        scale: 0.8
      }} animate={{
        scale: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-519" />
        </motion.div>}
    </section>;
}